import { Phone, Clock, ShieldCheck, CheckCircle2, MapPin } from "lucide-react";
import { SITE } from "@/lib/siteConfig";
import { trackConversion } from "@/lib/gtag";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-white"
    >
      {/* Thin emergency ribbon */}
      <div className="bg-[#0A0A0A] text-white text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="font-semibold tracking-wide">
              URGENCE EN COURS ? Intervention {SITE.responseTime}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: Content */}
          <div className="lg:col-span-7 fade-up">
            <div
              data-testid="geo-chip"
              className="inline-flex items-center gap-2 bg-red-50 border border-red-200 px-3 py-1.5 text-red-700 font-bold uppercase tracking-[0.15em] text-xs mb-6"
            >
              <MapPin className="w-3 h-3" strokeWidth={2.5} />
              Serrurerie d'urgence · Disponible maintenant
            </div>

            <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.92] text-[#0A0A0A]">
              Porte claquée ?<br />
              <span className="relative inline-block">
                <span className="relative z-10 text-white px-3">On arrive.</span>
                <span className="absolute inset-0 bg-red-600 -skew-x-6" />
              </span>
              <br />
              En moins de 30 min.
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-xl leading-relaxed">
              Serrurier professionnel disponible <strong className="text-slate-900">24h/24, 7j/7</strong>{" "}
              près de chez vous. Devis clair au téléphone, intervention sans dégât, tarif respecté.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={SITE.phoneHref}
                onClick={() => trackConversion("phone_click_hero")}
                data-testid="call-cta-hero"
                className="inline-flex items-center justify-center gap-3 bg-red-600 hover:bg-red-700 text-white px-7 py-5 font-display font-black uppercase text-lg tracking-tight transition-all hover:-translate-y-0.5 shadow-[0_6px_0_#991B1B] hover:shadow-[0_3px_0_#991B1B] pulse-ring"
              >
                <Phone className="w-6 h-6" strokeWidth={2.5} />
                Appeler {SITE.phone}
              </a>
              <a
                href="#contact"
                data-testid="quote-cta-hero"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-[#0A0A0A] px-7 py-5 font-bold uppercase text-sm tracking-[0.15em] border-2 border-[#0A0A0A] transition-all"
              >
                Demander un devis
              </a>
            </div>

            {/* Trust badges */}
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              <TrustBadge icon={Clock} label="Intervention" value="< 30 min" />
              <TrustBadge icon={CheckCircle2} label="Devis" value="Gratuit" />
              <TrustBadge icon={ShieldCheck} label="Agréé" value="Assurance" />
            </div>
          </div>

          {/* Right: Image card */}
          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden border border-black/10 relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_emergency-lock-3/artifacts/y0j6m34t_photo-1558002038-1055907df827.jpeg"
                  alt="Serrure connectée smart lock - SOS Serrurier Express"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </div>

              {/* Floating stat card */}
              <div className="absolute -bottom-8 -left-4 sm:-left-8 bg-[#0A0A0A] text-white p-5 sm:p-6 border border-white/10 shadow-2xl max-w-[260px]">
                <div className="text-[10px] uppercase tracking-[0.2em] text-red-400 font-bold mb-2">
                  Note moyenne vérifiée
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display font-black text-4xl">4,9</span>
                  <span className="text-slate-400 text-sm">/ 5</span>
                </div>
                <div className="mt-1 text-xs text-slate-400">Sur 1 200+ avis clients</div>
              </div>

              {/* Top sticker */}
              <div className="absolute -top-4 -right-4 bg-red-600 text-white px-4 py-3 font-display font-black uppercase text-sm tracking-tight rotate-3 shadow-lg">
                Ouvert<br />maintenant
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBadge({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5 py-3 border-t-2 border-[#0A0A0A]">
      <Icon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
      <div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-bold">
          {label}
        </div>
        <div className="font-display font-bold text-sm sm:text-base text-[#0A0A0A]">
          {value}
        </div>
      </div>
    </div>
  );
}
